import { Badge, Box, Heading, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import {
  BuilderContext,
  formatHours,
  formatPercent,
  statusMeta,
  formatTimestamp,
} from "../ai-weekly-template-builder.utils";

type Summary = BuilderContext["recentSuggestions"][number];

interface Props {
  suggestions: Summary[];
  selectedId: Summary["id"] | null;
  onSelect: (suggestion: Summary) => void;
}

/**
 * Past suggestions for this authorization. Deliberately compact: this sits above
 * the form, and its job is to answer "has someone already tried this, and how
 * did it go" without the coordinator opening anything.
 */
export default function AiWeeklyTemplateHistoryList(props: Props) {
  return (
    // flexShrink is load-bearing here. This is a direct flex child of a
    // height-constrained ModalBody, and `overflowX="auto"` makes the computed
    // overflow-y `auto` as well, which switches off a flex item's automatic
    // minimum size. Without this the list collapses to zero height while still
    // being present in the DOM — visible to `innerText`, invisible on screen.
    <Box flexShrink={0} overflowX="auto">
      <Heading pb={1} size="sm">
        Previous suggestions ({props.suggestions.length})
      </Heading>
      <Text color="gray.600" fontSize="sm" pb={2}>
        This authorization has been worked on before. Click one to see exactly what it proposed, or
        build a new one below.
      </Text>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>When</Th>
            <Th>By</Th>
            <Th>Outcome</Th>
            <Th>Applied</Th>
            <Th isNumeric>Weekly</Th>
            <Th isNumeric>Utilization</Th>
            <Th>Rules used</Th>
          </Tr>
        </Thead>
        <Tbody>
          {props.suggestions.map((suggestion) => {
            const meta = statusMeta(suggestion.status);
            const isSelected = props.selectedId === suggestion.id;
            return (
              <Tr
                key={Number(suggestion.id)}
                bg={isSelected ? "blue.50" : undefined}
                cursor="pointer"
                onClick={() => props.onSelect(suggestion)}
              >
                <Td whiteSpace="nowrap">
                  {formatTimestamp(suggestion.createdAt)}
                  {/*
                    Marked, but not numbered. This list is a flat window over one
                    authorization, so a revision's earlier turns may have fallen
                    off the end of it — a version number counted from what happens
                    to be visible would be wrong. Opening the row shows the whole
                    conversation with real numbering.
                  */}
                  {suggestion.parentSuggestionId !== null && (
                    <Badge colorScheme="blue" ml={2} variant="subtle">
                      Revision
                    </Badge>
                  )}
                </Td>
                <Td>{suggestion.createdByName}</Td>
                <Td>
                  <Badge
                    colorScheme={
                      meta.alert === "success"
                        ? "green"
                        : meta.alert === "error"
                        ? "red"
                        : meta.alert === "warning"
                        ? "orange"
                        : "blue"
                    }
                    variant="subtle"
                  >
                    {meta.title}
                  </Badge>
                  {suggestion.errorCount > 0 && (
                    <Text as="span" color="red.500" fontSize="xs">
                      {" "}
                      {suggestion.errorCount} error{suggestion.errorCount === 1 ? "" : "s"}
                    </Text>
                  )}
                  {suggestion.warningCount > 0 && (
                    <Text as="span" color="orange.500" fontSize="xs">
                      {" "}
                      {suggestion.warningCount} note{suggestion.warningCount === 1 ? "" : "s"}
                    </Text>
                  )}
                </Td>
                <Td whiteSpace="nowrap">
                  {suggestion.appliedAt === null ? (
                    <Text as="span" color="gray.400">
                      —
                    </Text>
                  ) : (
                    <>
                      {/*
                        Purple, not green: the Outcome cell next door is already
                        showing a green "Verified" badge, and two greens side by
                        side stop meaning anything.
                      */}
                      <Badge colorScheme="purple" variant="subtle">
                        Applied
                      </Badge>
                      <Text color="gray.600" fontSize="xs">
                        {formatTimestamp(suggestion.appliedAt)}
                      </Text>
                      {suggestion.appliedByName !== null && (
                        <Text color="gray.600" fontSize="xs">
                          {suggestion.appliedByName}
                        </Text>
                      )}
                    </>
                  )}
                </Td>
                <Td isNumeric>{formatHours(suggestion.weeklyTotalMinutes)}</Td>
                <Td isNumeric>
                  {formatPercent(
                    suggestion.realisticUtilizationPercent ?? suggestion.utilizationPercent
                  )}
                </Td>
                <Td fontSize="xs" maxW="260px">
                  {suggestion.schedulingRules === null ? (
                    <Text as="span" color="gray.400">
                      none
                    </Text>
                  ) : (
                    <Text noOfLines={2}>{suggestion.schedulingRules}</Text>
                  )}
                  {/* What was asked for on top of those rules, for a revision. */}
                  {suggestion.feedback !== null && (
                    <Text color="blue.600" noOfLines={2} pt={1}>
                      then: {suggestion.feedback}
                    </Text>
                  )}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}
